/**
 * Notifications Store
 *
 * Framework-agnostic Zustand store for managing notification state
 * and session-level notification lifecycle.
 *
 * The store is the live channel's memory, not a cache: it is seeded once per
 * signed-in session from the server, kept current by socket arrivals, and read
 * from memory when the bell opens. It is deliberately NOT persisted — the list
 * belongs to one account, and a persisted copy outlived that account's session
 * (docs/roadmap/session-scoped-state.md, P1). The session boundary empties it
 * whenever the account changes.
 */

import { immer } from 'zustand/middleware/immer';
import { createStore } from 'zustand/vanilla';

import { registerSessionScoped } from './session-scope';
import { syncReadSessionUser } from './store-sync';
import { NOTIFICATION_CONSTANTS } from '../config/constants';
import { dayjs } from '../utils/date';
import { getCategory } from '../utils/notification.utils';

import type {
  WebSocketStatus,
  NotificationData,
  NotificationItem,
  RawNotification,
} from '../types/notification.types';

// Re-export types for convenience
export type { NotificationData, NotificationItem, RawNotification };

// =============================================================================
// STORE INTERFACES
// =============================================================================

interface NotificationStoreState {
  // Connection state
  connectionStatus: WebSocketStatus;
  isConnecting: boolean;
  isConnected: boolean;
  connectionError: string | null;
  lastConnectedAt: number | null;
  reconnectAttempts: number;

  // Notification data
  notifications: NotificationItem[];
  unreadCount: number;
  totalCount: number;

  // Settings
  maxNotifications: number;
  autoMarkAsRead: boolean;
  soundEnabled: boolean;
}

interface NotificationStoreActions {
  // Connection management
  setConnectionStatus: (status: WebSocketStatus) => unknown;
  setConnectionError: (error: string | null) => unknown;
  incrementReconnectAttempts: () => unknown;
  resetReconnectAttempts: () => unknown;
  setConnected: (connected: boolean) => unknown;

  // Notification management
  addNotification: (notification: RawNotification) => unknown;
  hydrateNotifications: (
    notifications: RawNotification[],
    unreadTotal?: number
  ) => unknown;
  markAsRead: (notificationId: string) => unknown;
  markAsUnread: (notificationId: string) => unknown;
  markAllAsRead: () => unknown;
  removeNotification: (notificationId: string) => unknown;
  clearNotifications: () => unknown;
  clearOldNotifications: (maxAge?: number) => unknown;

  // Settings
  setMaxNotifications: (max: number) => unknown;
  setAutoMarkAsRead: (enabled: boolean) => unknown;
  setSoundEnabled: (enabled: boolean) => unknown;

  // Utilities
  getNotificationById: (id: string) => NotificationItem | undefined;
  getUnreadNotifications: () => NotificationItem[];
  getNotificationsByType: (type: string) => NotificationItem[];

  // Store management
  reset: () => unknown;
}

type NotificationStore = NotificationStoreState & NotificationStoreActions;

// =============================================================================
// CONSTANTS
// =============================================================================

const DEFAULT_MAX_NOTIFICATIONS = 100;
const OLD_NOTIFICATION_THRESHOLD = 7 * 24 * 60 * 60 * 1000; // 7 days

// =============================================================================
// STORE IMPLEMENTATION
// =============================================================================

export const notificationStore = createStore<NotificationStore>()(
  immer((set, get) => ({
    // =======================================================================
    // INITIAL STATE
    // =======================================================================

    // Connection state
    connectionStatus: 'disconnected',
    isConnecting: false,
    isConnected: false,
    connectionError: null,
    lastConnectedAt: null,
    reconnectAttempts: 0,

    // Notification data
    notifications: [],
    unreadCount: 0,
    totalCount: 0,

    // Settings
    maxNotifications: DEFAULT_MAX_NOTIFICATIONS,
    autoMarkAsRead: false,
    soundEnabled: true,

    // =======================================================================
    // CONNECTION MANAGEMENT
    // =======================================================================

    setConnectionStatus: (status: WebSocketStatus): void => {
      set((state) => {
        // Override status if notifications are disabled
        if (!NOTIFICATION_CONSTANTS.LIVE_NOTIFICATIONS) {
          state.connectionStatus = 'disconnected';
          state.isConnecting = false;
          state.isConnected = false;
          state.connectionError = 'Notifications disabled';
          return;
        }

        state.connectionStatus = status;
        state.isConnecting =
          status === 'connecting' || status === 'reconnecting';
        state.isConnected = status === 'connected';

        if (status === 'connected') {
          state.lastConnectedAt = dayjs().valueOf();
          state.reconnectAttempts = 0;
          state.connectionError = null;
        }
      });
    },

    setConnectionError: (error: string | null): void => {
      set((state) => {
        state.connectionError = error;
        if (error) {
          state.connectionStatus = 'error';
          state.isConnected = false;
          state.isConnecting = false;
        }
      });
    },

    incrementReconnectAttempts: (): void => {
      set((state) => {
        state.reconnectAttempts += 1;
      });
    },

    resetReconnectAttempts: (): void => {
      set((state) => {
        state.reconnectAttempts = 0;
      });
    },

    setConnected: (connected: boolean): void => {
      set((state) => {
        state.isConnected = connected;
        state.connectionStatus = connected ? 'connected' : 'disconnected';
        if (connected) {
          state.lastConnectedAt = dayjs().valueOf();
          state.reconnectAttempts = 0;
          state.connectionError = null;
        }
      });
    },

    // =======================================================================
    // NOTIFICATION MANAGEMENT
    // =======================================================================

    addNotification: (notification: RawNotification): void => {
      let arrived = false;
      set((state) => {
        // Check kill switch before adding notifications
        if (!NOTIFICATION_CONSTANTS.LIVE_NOTIFICATIONS) {
          console.log(
            '[NotificationStore] Notifications disabled - ignoring incoming notification'
          );
          return;
        }

        // Check if notification already exists
        const existingIndex = state.notifications.findIndex(
          (n) => n.id === notification.id
        );

        if (existingIndex >= 0) {
          // Update existing notification, adjusting the unread tally only for
          // a genuine read-state transition.
          const wasUnread = state.notifications[existingIndex].read_at === null;
          const isUnread = notification.read_at === null;
          if (wasUnread && !isUnread) {
            state.unreadCount = Math.max(0, state.unreadCount - 1);
          } else if (!wasUnread && isUnread) {
            state.unreadCount += 1;
          }
          state.notifications[existingIndex] = notification;
        } else {
          arrived = true;
          // Add new notification at the beginning
          state.notifications.unshift(notification);
          if (notification.read_at === null) {
            state.unreadCount += 1;
          }

          // Trim notifications if we exceed max. unreadCount is intentionally
          // left untouched here: it tracks the server-wide unread total
          // (seeded via hydrateNotifications), so an item trimmed out of the
          // in-memory window may still be unread on the server.
          if (state.notifications.length > state.maxNotifications) {
            state.notifications = state.notifications.slice(
              0,
              state.maxNotifications
            );
          }
        }

        state.totalCount = state.notifications.length;

        // Auto-mark as read if enabled
        if (state.autoMarkAsRead && notification.read_at === null) {
          const notificationIndex = state.notifications.findIndex(
            (n) => n.id === notification.id
          );
          if (
            notificationIndex >= 0 &&
            state.notifications[notificationIndex].read_at === null
          ) {
            state.notifications[notificationIndex].read_at =
              dayjs().toISOString();
            state.unreadCount = Math.max(0, state.unreadCount - 1);
          }
        }
      });

      // An account notice says the account changed elsewhere: read it now,
      // without the 30-second wait a return has (social sign-in PRD, 10.3).
      if (arrived && getCategory(notification) === 'account') {
        syncReadSessionUser();
      }
    },

    hydrateNotifications: (
      incoming: RawNotification[],
      unreadTotal?: number
    ): void => {
      set((state) => {
        // Respect the kill switch, same as the live ingest path.
        if (!NOTIFICATION_CONSTANTS.LIVE_NOTIFICATIONS) {
          return;
        }

        const hasIncoming = Array.isArray(incoming) && incoming.length > 0;
        if (!hasIncoming && unreadTotal === undefined) {
          return;
        }

        if (hasIncoming) {
          // Merge server history with what's already in the store. Existing
          // entries win over their server copies so live arrivals and
          // optimistic read/unread changes aren't clobbered by slightly-stale
          // history pulled from the REST endpoint.
          const byId = new Map<string, NotificationItem>();
          incoming.forEach((n) => byId.set(n.id, n));
          state.notifications.forEach((n) => byId.set(n.id, n));

          const merged = Array.from(byId.values()).sort(
            (a, b) =>
              dayjs(b.created_at).valueOf() - dayjs(a.created_at).valueOf()
          );

          state.notifications =
            merged.length > state.maxNotifications
              ? merged.slice(0, state.maxNotifications)
              : merged;

          state.totalCount = state.notifications.length;
        }

        // Prefer the server-reported unread total so the badge can render
        // "99+" even when more notifications are unread than fit in the
        // in-memory window. Fall back to the in-window count when no total
        // is supplied.
        state.unreadCount =
          unreadTotal !== undefined
            ? Math.max(0, unreadTotal)
            : state.notifications.filter((n) => n.read_at === null).length;
      });
    },

    markAsRead: (notificationId: string): void => {
      set((state) => {
        const notification = state.notifications.find(
          (n) => n.id === notificationId
        );
        if (notification && notification.read_at === null) {
          notification.read_at = dayjs().toISOString();
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      });
    },

    markAsUnread: (notificationId: string): void => {
      set((state) => {
        const notification = state.notifications.find(
          (n) => n.id === notificationId
        );
        if (notification && notification.read_at !== null) {
          notification.read_at = null;
          state.unreadCount += 1;
        }
      });
    },

    markAllAsRead: (): void => {
      set((state) => {
        const currentTime = dayjs().toISOString();
        state.notifications.forEach((notification) => {
          if (notification.read_at === null) {
            notification.read_at = currentTime;
          }
        });
        state.unreadCount = 0;
      });
    },

    removeNotification: (notificationId: string): void => {
      set((state) => {
        const notificationIndex = state.notifications.findIndex(
          (n) => n.id === notificationId
        );
        if (notificationIndex >= 0) {
          const wasUnread =
            state.notifications[notificationIndex].read_at === null;
          state.notifications.splice(notificationIndex, 1);
          state.totalCount = state.notifications.length;
          if (wasUnread) {
            state.unreadCount = Math.max(0, state.unreadCount - 1);
          }
        }
      });
    },

    clearNotifications: (): void => {
      set((state) => {
        state.notifications = [];
        state.unreadCount = 0;
        state.totalCount = 0;
      });
    },

    clearOldNotifications: (maxAge = OLD_NOTIFICATION_THRESHOLD): void => {
      set((state) => {
        const cutoffTime = dayjs().valueOf() - maxAge;

        state.notifications = state.notifications.filter((notification) => {
          const createdAt = dayjs(notification.created_at).valueOf();
          return createdAt > cutoffTime;
        });

        state.totalCount = state.notifications.length;
        state.unreadCount = state.notifications.filter(
          (n) => n.read_at === null
        ).length;
      });
    },

    // =======================================================================
    // SETTINGS
    // =======================================================================

    setMaxNotifications: (max: number): void => {
      set((state) => {
        state.maxNotifications = Math.max(1, max);
        // Trim notifications if current count exceeds new max
        if (state.notifications.length > state.maxNotifications) {
          const removedNotifications = state.notifications.slice(
            state.maxNotifications
          );
          const removedUnreadCount = removedNotifications.filter(
            (n) => n.read_at === null
          ).length;

          state.notifications = state.notifications.slice(
            0,
            state.maxNotifications
          );
          state.totalCount = state.notifications.length;
          state.unreadCount = Math.max(
            0,
            state.unreadCount - removedUnreadCount
          );
        }
      });
    },

    setAutoMarkAsRead: (enabled: boolean): void => {
      set((state) => {
        state.autoMarkAsRead = enabled;
      });
    },

    setSoundEnabled: (enabled: boolean): void => {
      set((state) => {
        state.soundEnabled = enabled;
      });
    },

    // =======================================================================
    // UTILITIES
    // =======================================================================

    getNotificationById: (id: string): NotificationItem | undefined => {
      return get().notifications.find((n) => n.id === id);
    },

    getUnreadNotifications: (): NotificationItem[] => {
      return get().notifications.filter((n) => n.read_at === null);
    },

    getNotificationsByType: (type: string): NotificationItem[] => {
      return get().notifications.filter((n) => n.type === type);
    },

    // =======================================================================
    // STORE MANAGEMENT
    // =======================================================================

    reset: (): void => {
      set((state) => {
        // Reset connection state
        state.connectionStatus = 'disconnected';
        state.isConnecting = false;
        state.isConnected = false;
        state.connectionError = null;
        state.lastConnectedAt = null;
        state.reconnectAttempts = 0;

        // Clear notification data
        state.notifications = [];
        state.unreadCount = 0;
        state.totalCount = 0;

        // Reset settings to defaults
        state.maxNotifications = DEFAULT_MAX_NOTIFICATIONS;
        state.autoMarkAsRead = false;
        state.soundEnabled = true;
      });
    },
  }))
);

// =============================================================================
// SESSION SCOPE
// =============================================================================

// The list and its counts are one account's. Connection state belongs to the
// socket provider, and the settings are the device's, so `clearNotifications`
// and not `reset`.
registerSessionScoped('notificationStore', () => {
  notificationStore.getState().clearNotifications();
});

// =============================================================================
// SELECTORS
// =============================================================================

/**
 * ⚠️ IMPORTANT (Zustand + React):
 *
 * These selectors return object literals (e.g. `{ unread, total, hasUnread }`).
 * In Zustand, selectors are compared with `Object.is` by default — meaning a new
 * object reference will be treated as "changed" even if the values are the same.
 *
 * To avoid unnecessary re-renders (and in some cases React warnings like
 * "getSnapshot should be cached" / infinite update loops), consume object-returning
 * selectors with a shallow equality function, or select primitives instead.
 *
 * Example:
 *   import { shallow } from 'zustand/shallow';
 *   const counts = useNotificationStore(selectNotificationCounts, shallow);
 *
 * Or:
 *   const unread = useNotificationStore((s) => s.unreadCount);
 */

/**
 * Get connection status
 */
export const selectConnectionStatus = (
  state: NotificationStore
): {
  status: WebSocketStatus;
  isConnecting: boolean;
  isConnected: boolean;
  error: string | null;
  lastConnectedAt: number | null;
  reconnectAttempts: number;
} => ({
  status: state.connectionStatus,
  isConnecting: state.isConnecting,
  isConnected: state.isConnected,
  error: state.connectionError,
  lastConnectedAt: state.lastConnectedAt,
  reconnectAttempts: state.reconnectAttempts,
});

/**
 * Get notification counts
 */
export const selectNotificationCounts = (
  state: NotificationStore
): {
  unread: number;
  total: number;
  hasUnread: boolean;
} => ({
  unread: state.unreadCount,
  total: state.totalCount,
  hasUnread: state.unreadCount > 0,
});

/**
 * Get notification settings
 */
export const selectNotificationSettings = (
  state: NotificationStore
): {
  maxNotifications: number;
  autoMarkAsRead: boolean;
  soundEnabled: boolean;
} => ({
  maxNotifications: state.maxNotifications,
  autoMarkAsRead: state.autoMarkAsRead,
  soundEnabled: state.soundEnabled,
});

/**
 * Get notifications with pagination
 */
export const selectNotificationsPaginated = (
  state: NotificationStore,
  limit = 20,
  offset = 0
): { notifications: NotificationItem[]; hasMore: boolean; total: number } => {
  const notifications = state.notifications.slice(offset, offset + limit);
  const hasMore = offset + limit < state.totalCount;
  return { notifications, hasMore, total: state.totalCount };
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get connection status
 */
export const getConnectionStatus = (): WebSocketStatus => {
  return notificationStore.getState().connectionStatus;
};

/**
 * Get unread count
 */
export const getUnreadCount = (): number => {
  return notificationStore.getState().unreadCount;
};

/**
 * Check if connected
 */
export const isNotificationSystemConnected = (): boolean => {
  return notificationStore.getState().isConnected;
};

/**
 * Get all notifications
 */
export const getAllNotifications = (): NotificationItem[] => {
  return notificationStore.getState().notifications;
};

/**
 * Get notification by ID
 */
export const getNotificationById = (
  id: string
): NotificationItem | undefined => {
  return notificationStore.getState().getNotificationById(id);
};
